import React from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, ArrowLeft } from "lucide-react";
/**
 * GDPR-aligned public Privacy Policy for Folio Press.
 * Route: /privacy
 *
 * Structured to satisfy Arts. 13 & 14 GDPR (information at collection),
 * Arts. 15–22 (data subject rights), Art. 6 (lawful basis), Art. 7 (consent),
 * Recital 39 (transparency) and the UK ICO / EU EDPB transparency guidelines.
 */
export default function Privacy() {
  const updated = "23 February 2026";

  return (
    <div data-testid="privacy-page" className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24 text-ink">
      <Link
        to="/"
        data-testid="privacy-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted_ink hover:text-terracotta"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back to Folio
      </Link>

      <div className="flex items-center gap-3 mt-8 mb-4">
        <Shield className="w-6 h-6 text-terracotta" strokeWidth={1.5} />
        <div className="label-eyebrow">Privacy notice</div>
      </div>
      <h1 className="font-serif text-5xl md:text-6xl leading-tight">Privacy policy</h1>
      <p className="text-muted_ink mt-4 text-sm">Last updated: {updated}</p>

      <div className="mt-12 space-y-10 text-base leading-relaxed">

        <Section title="1. Who we are (data controller)">
          <p>
            Folio Press (“<strong>Folio</strong>”, “we”, “us”) publishes and sells digital ebooks at{" "}
            <a href="https://folio.llc" className="underline hover:text-terracotta">folio.llc</a>.
            For the purposes of the EU General Data Protection Regulation (GDPR) and the UK GDPR,
            we are the <strong>data controller</strong> of the personal data described below.
          </p>
          <p className="mt-3">
            Questions or requests about your data can be sent to{" "}
            <a href="mailto:privacy@folio.llc" className="underline hover:text-terracotta">
              privacy@folio.llc
            </a>.
          </p>
        </Section>

        <Section title="2. What we collect and why">
          <p className="mb-4">
            We only collect what we need to run the store, deliver your books, and improve the reading experience.
          </p>
          <Table
            rows={[
              ["Account data", "Email, hashed password, display name", "To sign you in and secure your library", "Contract (Art. 6(1)(b))"],
              ["Purchase data", "Order ID, book(s) purchased, amount, currency, timestamps", "To fulfil orders, provide receipts, comply with tax law", "Contract + Legal obligation (Art. 6(1)(b), (c))"],
              ["Payment metadata", "Stripe/PayPal session IDs and last-4 card digits (we never see the full card)", "To process payments and prevent fraud", "Contract (Art. 6(1)(b))"],
              ["Reading activity", "Books opened, reading progress, bookmarks, highlights, streaks", "To restore your place across devices and power streaks/milestones", "Contract + Legitimate interest (Art. 6(1)(b), (f))"],
              ["Marketing data", "Email address, unsubscribe token", "To send Letters from the press when you opt in", "Consent (Art. 6(1)(a))"],
              ["Analytics data", "Anonymised page views, device type, referrer, GA client ID", "To understand traffic patterns and improve the shop", "Consent (Art. 6(1)(a)) — denied by default"],
              ["Support data", "Feedback messages you send us", "To reply to you and fix bugs", "Legitimate interest (Art. 6(1)(f))"],
            ]}
          />
        </Section>

        <Section title="3. Cookies and similar technologies">
          <p>
            All non-essential cookies (analytics, advertising) are <strong>blocked by default</strong> under
            Google Consent Mode v2 until you accept them through our Secure Privacy consent banner.
            You can change your choice at any time via the <strong>Cookie preferences</strong> link in the footer.
          </p>
          <p className="mt-3">
            The full inventory of cookies we set (name, provider, purpose, duration) lives on our{" "}
            <Link to="/cookies" className="underline hover:text-terracotta">cookie policy</Link>{" "}
            page and is mirrored in the banner's preference centre.
          </p>
        </Section>

        <Section title="4. Third parties we share data with">
          <p className="mb-3">We only share personal data with providers that help us run Folio. Each is bound by a data-processing agreement.</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Stripe, Inc.</strong> — card payments (USA, adequacy via EU-US Data Privacy Framework).</li>
            <li><strong>PayPal (Europe) S.à r.l.</strong> — alternative payments, when enabled (Luxembourg).</li>
            <li><strong>Resend</strong> — transactional and marketing emails (USA, DPF).</li>
            <li><strong>Google LLC</strong> — Google Analytics 4 (USA, DPF). Consent-gated, IP truncated, no ad-personalisation without opt-in.</li>
            <li><strong>Secure Privacy</strong> — cookie consent management (EU).</li>
            <li><strong>MongoDB Atlas</strong> — application database hosting (EU region).</li>
          </ul>
          <p className="mt-3">
            We do <strong>not</strong> sell your personal data. We do not use automated decision-making with legal effect.
          </p>
        </Section>

        <Section title="5. International transfers">
          <p>
            Some processors are based outside the EU/EEA (mainly the USA). Transfers rely on:
            (i) the EU-US Data Privacy Framework where the recipient is certified, or
            (ii) the European Commission's Standard Contractual Clauses (SCCs) with supplementary safeguards.
            Copies of the relevant safeguards are available on request from{" "}
            <a href="mailto:privacy@folio.llc" className="underline hover:text-terracotta">privacy@folio.llc</a>.
          </p>
        </Section>

        <Section title="6. How long we keep data">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account and library</strong> — while your account is active. Deleted within 30 days of a deletion request.</li>
            <li><strong>Order records</strong> — 7 years, to meet tax and accounting obligations.</li>
            <li><strong>Newsletter</strong> — until you unsubscribe. One-click link in every email.</li>
            <li><strong>Analytics</strong> — 14 months (default GA4 retention).</li>
            <li><strong>Support messages</strong> — 24 months, then anonymised.</li>
          </ul>
        </Section>

        <Section title="7. Your rights under the GDPR">
          <p className="mb-3">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Access</strong> the personal data we hold about you (Art. 15).</li>
            <li><strong>Rectify</strong> inaccurate or incomplete data (Art. 16).</li>
            <li><strong>Erase</strong> your data ("right to be forgotten", Art. 17).</li>
            <li><strong>Restrict</strong> processing in certain cases (Art. 18).</li>
            <li><strong>Data portability</strong> — receive your data in a machine-readable format (Art. 20).</li>
            <li><strong>Object</strong> to processing based on legitimate interest (Art. 21).</li>
            <li><strong>Withdraw consent</strong> at any time, without affecting past lawful processing (Art. 7(3)).</li>
            <li><strong>Lodge a complaint</strong> with your local supervisory authority — for EU/EEA readers, the list is at{" "}
              <a
                href="https://edpb.europa.eu/about-edpb/board/members_en"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-terracotta"
              >
                edpb.europa.eu
              </a>{" "}
              (Art. 77).
            </li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, write to{" "}
            <a href="mailto:privacy@folio.llc" className="underline hover:text-terracotta">privacy@folio.llc</a>.
            We respond within 30 days.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            Passwords are hashed with bcrypt. All traffic is served over HTTPS (TLS 1.2+).
            Card details are handled by our PCI-DSS-compliant payment processors — they never touch our servers.
          </p>
        </Section>

        <Section title="9. Children">
          <p>
            Folio is not directed at children under 16. We do not knowingly collect data from anyone in that age group.
            If you believe a child has provided us with personal data, contact us and we will delete it.
          </p>
        </Section>

        <Section title="10. Changes to this policy">
          <p>
            When we materially change this policy, we'll update the “Last updated” date above and,
            where required, notify you by email or an in-app banner before the change takes effect.
          </p>
        </Section>

        <div className="mt-10 pt-8 border-t border-edge flex items-center gap-3 text-sm text-muted_ink">
          <Mail className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
          Contact:{" "}
          <a href="mailto:privacy@folio.llc" className="underline hover:text-terracotta">
            privacy@folio.llc
          </a>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl md:text-3xl mb-3">{title}</h2>
      <div className="text-muted_ink">{children}</div>
    </section>
  );
}

function Table({ rows }) {
  return (
    <div className="overflow-x-auto border border-edge">
      <table className="w-full text-sm">
        <thead className="bg-linen">
          <tr>
            <th className="text-left p-3 font-serif font-normal text-ink">Category</th>
            <th className="text-left p-3 font-serif font-normal text-ink">What it is</th>
            <th className="text-left p-3 font-serif font-normal text-ink">Why we process it</th>
            <th className="text-left p-3 font-serif font-normal text-ink">Lawful basis</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r[0]} className={i % 2 ? "bg-paper" : ""}>
              {r.map((cell, j) => (
                <td key={`${r[0]}-${j}`} className="p-3 align-top border-t border-edge">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
